import React, { useContext, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { NavLink, useLocation } from "react-router-dom";

import Drawer from "@mui/material/Drawer";
import { ROUTES } from "@/helpers/routes/routePaths";

import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import MuiListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { styled } from "@mui/material/styles";

import { GlobalContext } from "@/helpers/contexts/contexts";
import { ROLES } from "@/helpers/routes/rolesConfig";
import { settingSelector } from "@/shared/store/settingsSlice";
import RpImageTextInline from "@/components/RpImageTextInline";
import Feedback from "@/shared/components/feedback/Feedback";
import {
  USER_ROLE,
  BRAND_CONST,
  isBrandSubUserAllowed,
} from "@/config/constants";
import { EMAIL_CAMPAIGN_CONST } from "@/features/campaigns/email/utils/emailCampaignConstants";
import { IS_PROD, IS_WHITE_LABELED_DOMAIN } from "@/config/appConfig";
import useSocialPostings from "@/helpers/hooks/useSocialPostings";
import SocialPostingTabs from "./SocialPostingTabs";
const drawerWidth = 240;

const LayoutWrapper = styled("div")(({ theme }) => ({
  // root styles (if needed)
  display: "flex",
  width: "100%",
  boxSizing: "border-box",

  // Drawer styles
  "& .drawer": {
    [theme.breakpoints.up("lg")]: {
      width: drawerWidth,
      flexShrink: 0,
    },
  },

  // Side nav container
  "& .side-nav": {
    width: "100%",
    boxSizing: "border-box",
    overflow: "hidden",
  },
  "& .side-navWithSocialTabs": {
    paddingTop: "4.5rem",
  },

  // AppBar styles
  "& .appBar": {
    [theme.breakpoints.up("lg")]: {
      width: `calc(100% - ${drawerWidth}px)`,
      marginLeft: drawerWidth,
    },
    backgroundColor: "var(--neutral-200)",
    display: "flex",
    justifyContent: "space-between",
  },

  // Menu Button
  "& .menuButton": {
    marginRight: theme.spacing(2),
    [theme.breakpoints.up("lg")]: {
      display: "none",
    },
    color: (theme.vars || theme)?.palette?.primary?.main,
  },

  // Toolbar (can use mixins)
  "& .toolbar": {
    ...theme.mixins.toolbar,
    minWidth: "240px",
  },
  // List Item
  "& .listItem": {
    color: "var(--palette-navigation-text)",
    marginBottom: "5px",
    borderRadius: "var(--radius-control)",
    cursor: "pointer",
    "&:hover": {
      color: "var(--palette-navigation-text)",
      backgroundColor: "var(--palette-navigation-hoverSurface)",
    },
  },

  // Active List Item
  "& .listItem.activeListItem": {
    backgroundColor: "var(--palette-navigation-selectedSurface)",
    color: "var(--palette-navigation-text)",
    fontWeight: "900",
  },

  // List Item Icon
  "& .listItemIcon": {
    color: (theme.vars || theme).palette.primary.brand,
  },
}));

const CREATE_CAMPAIGN_ALLOWED_BRAND = ["BD845257", "BD577784"];

type SelectedBrand = {
  has_active_subscription_page?: boolean;
  id?: number | string;
  is_brand_request?: boolean;
  is_social_posting?: boolean;
  is_white_label?: boolean;
  system_generated_brand_id?: string;
};

type LoggedInUser = {
  is_sub_participant?: boolean;
};

type BrandSettings = {
  brandSettings?: {
    has_brand_resource?: boolean;
  };
};

type ResponsiveDrawerProps = {
  handleDrawerToggle?: () => void;
  mobileOpen?: boolean;
  setMobileOpen?: (_open: boolean) => void;
};

const ListItemButton = MuiListItemButton as React.ComponentType<
  Record<string, unknown>
>;

function ResponsiveDrawer({
  mobileOpen = false,
  handleDrawerToggle,
  setMobileOpen = () => undefined,
}: ResponsiveDrawerProps) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.down("lg"));
  const {
    selectedBrand,
    loggedInUser,
    loggedInUserRole: contextLoggedInUserRole,
  } = useContext(GlobalContext);
  const settings = useSelector(settingSelector) as BrandSettings;
  const currentBrand = selectedBrand as SelectedBrand | null | undefined;
  const currentUser = loggedInUser as LoggedInUser | null | undefined;
  const loggedInUserRole = String(contextLoggedInUserRole || "");
  const [FeedbackFormOpen, setFeedbackFormOpen] = useState(false);
  const location = useLocation();
  const { pathname } = location;

  const { navigateToSocialPostings } = useSocialPostings({
    closeSidebar: () => setMobileOpen(false),
    brandId: currentBrand?.id,
  });

  const hasSocialPosting =
    !!currentBrand?.is_social_posting &&
    !currentBrand?.is_white_label &&
    !IS_WHITE_LABELED_DOMAIN;
  const isParticipantUser = ROLES.PARTICIPANT.includes(loggedInUserRole);
  const canParticipantAccessSocialPosting =
    loggedInUserRole === USER_ROLE.PARTICIPANT.NAME &&
    !currentUser?.is_sub_participant;
  const isBrandSocialPostingUser = ROLES.BRAND.includes(loggedInUserRole);
  const isParentCompanySocialPostingUser =
    ROLES.PARENT_COMPANY.includes(loggedInUserRole);
  const canShowSocialPostingTabs =
    !!hasSocialPosting &&
    (canParticipantAccessSocialPosting ||
      isBrandSocialPostingUser ||
      isParentCompanySocialPostingUser);
  const canShowParticipantUserManagement =
    loggedInUserRole === USER_ROLE.PARTICIPANT.NAME &&
    !currentUser?.is_sub_participant &&
    isBrandSubUserAllowed(currentBrand);
  const isRouteActive = useCallback(
    (path: string, additionalPaths: string[] = []) => {
      // Convert route pattern to regex (e.g., /path/:id -> /path/[^/]+)
      const patternToRegex = (pattern: string) => {
        const regexPattern = pattern
          .split("/")
          .map((segment) => (segment.startsWith(":") ? "[^/]+" : segment))
          .join("/");
        return new RegExp(`^${regexPattern}(\\?|#|$)`);
      };

      const pathMatches = (p: string) => {
        if (pathname === p) return true;
        const regex = patternToRegex(p);
        return regex.test(pathname);
      };

      const mainPathMatches = pathMatches(path);

      const additionalPathMatches = additionalPaths.some((p) => pathMatches(p));

      return mainPathMatches || additionalPathMatches;
    },
    [pathname]
  );

  const getRouteClassName = useCallback(
    (path: string, additionalPaths: string[] = []) => {
      const baseClass = "listItem";
      const activeClass = isRouteActive(path, additionalPaths)
        ? "activeListItem"
        : "";
      return `${baseClass} ${activeClass}`.trim();
    },
    [isRouteActive]
  );

  const handleOpenDialog = useCallback(() => {
    setFeedbackFormOpen(true);
    setMobileOpen(false);
  }, [setFeedbackFormOpen, setMobileOpen]);

  const handleCloseDialog = useCallback(() => {
    setFeedbackFormOpen(false);
  }, [setFeedbackFormOpen]);

  const drawer = (
    <LayoutWrapper>
      <div
        data-testid={
          canShowSocialPostingTabs ? "social-posting-side-nav" : undefined
        }
        className={
          canShowSocialPostingTabs
            ? "side-nav side-navWithSocialTabs"
            : "side-nav"
        }
      >
        {!canShowSocialPostingTabs ? (
          <div className="toolbar" style={{ marginTop: "-62px" }} />
        ) : null}

        {canShowSocialPostingTabs ? (
          <SocialPostingTabs onSocialPostsClick={navigateToSocialPostings} />
        ) : null}

        <List
          className={canShowSocialPostingTabs ? "" : "mt-2"}
          disablePadding={canShowSocialPostingTabs}
        >
          {loggedInUserRole === USER_ROLE.PARENT_COMPANY.NAME ||
          loggedInUserRole === USER_ROLE.PARENT_COMPANY_USER.NAME ||
          loggedInUserRole === USER_ROLE.PARENT_COMPANY_SUB_USER.NAME ? (
            <>
              <ListItemButton
                component={NavLink}
                to={ROUTES.OVERVIEW_DASHBOARD}
                classes={{ root: getRouteClassName(ROUTES.OVERVIEW_DASHBOARD) }}
              >
                <ListItemIcon className="listItemIcon">
                  <RpImageTextInline svgIcon="overview_dashboard_nav" />
                </ListItemIcon>
                <ListItemText primary="Overview Dashboard" />
              </ListItemButton>
            </>
          ) : null}

          <ListItemButton
            component={NavLink}
            to={ROUTES.DASHBOARD}
            classes={{ root: getRouteClassName(ROUTES.DASHBOARD) }}
          >
            <ListItemIcon className="listItemIcon">
              <RpImageTextInline svgIcon="dashboard_nav" />
            </ListItemIcon>
            <ListItemText
              primary={
                loggedInUserRole === USER_ROLE.PARENT_COMPANY.NAME ||
                loggedInUserRole === USER_ROLE.PARENT_COMPANY_USER.NAME ||
                loggedInUserRole === USER_ROLE.PARENT_COMPANY_SUB_USER.NAME
                  ? "Brand Dashboard"
                  : "Dashboard"
              }
            />
          </ListItemButton>
          {isParticipantUser ? (
            <>
              <ListItem>
                <ListItemText
                  className="user-role nav-sections"
                  primary="Launch Advertising"
                />
              </ListItem>
              <ListItemButton
                component={NavLink}
                to={ROUTES.LAUNCH_CAMPAIGN}
                classes={{
                  root: getRouteClassName(ROUTES.LAUNCH_CAMPAIGN, [
                    ROUTES.JOIN_CAMPAIGN,
                    ROUTES.CONFIRM,
                  ]),
                }}
              >
                <ListItemIcon className="listItemIcon">
                  <RpImageTextInline svgIcon="file_nav" />
                </ListItemIcon>
                <ListItemText primary="Launch Digital Campaign" />
              </ListItemButton>
              <ListItemButton
                component={NavLink}
                to={ROUTES.LAUNCH_DIRECT_MAIL}
                classes={{
                  root: getRouteClassName(ROUTES.LAUNCH_DIRECT_MAIL, [
                    ROUTES.JOIN_POSTCARD_CAMPAIGN,
                    ROUTES.POSTCARD_CONFIRM,
                    ROUTES.RRD_JOIN_CAMPAIGN_CONFIRM,
                  ]),
                }}
              >
                <ListItemIcon className="listItemIcon">
                  <RpImageTextInline svgIcon="file_nav" />
                </ListItemIcon>
                <ListItemText primary="Launch Direct Mail" />
              </ListItemButton>
              {EMAIL_CAMPAIGN_CONST.AVAILABLE_TO.includes(
                IS_PROD ? currentBrand?.id : "all"
              ) ? (
                <ListItemButton
                  component={NavLink}
                  to={ROUTES.EMAIL_CAMPAIGNS}
                  classes={{ root: getRouteClassName(ROUTES.EMAIL_CAMPAIGNS) }}
                >
                  <ListItemIcon className="listItemIcon">
                    <RpImageTextInline svgIcon="file_nav" />
                  </ListItemIcon>
                  <ListItemText primary="Email Campaigns" />
                </ListItemButton>
              ) : null}
              <ListItem>
                <ListItemText
                  className="user-role nav-sections"
                  primary="Campaign Management"
                />
              </ListItem>
              <ListItemButton
                component={NavLink}
                to={ROUTES.DIGITAL_CAMPAIGNS}
                classes={{
                  root: getRouteClassName(ROUTES.DIGITAL_CAMPAIGNS, [
                    ROUTES.DIGITAL_CAMPAIGNS,
                    ROUTES.DIGITAL_CAMPAIGN_REPORT,
                  ]),
                }}
              >
                <ListItemIcon className="listItemIcon">
                  <RpImageTextInline svgIcon="camp_reports" />
                </ListItemIcon>
                <ListItemText primary="Digital Campaign Management" />
              </ListItemButton>
              <ListItemButton
                component={NavLink}
                to={ROUTES.DIRECT_MAIL}
                classes={{
                  root: getRouteClassName(ROUTES.DIRECT_MAIL, [
                    ROUTES.DIRECT_MAIL_REPORT,
                  ]),
                }}
              >
                <ListItemIcon className="listItemIcon">
                  <RpImageTextInline svgIcon="direct_mail_reports" />
                </ListItemIcon>
                <ListItemText primary="Direct Mail Reports" />
              </ListItemButton>
              {EMAIL_CAMPAIGN_CONST.AVAILABLE_TO.includes(
                IS_PROD ? currentBrand?.id : "all"
              ) ? (
                <ListItemButton
                  component={NavLink}
                  to={ROUTES.EMAIL_CAMPAIGN_REPORT}
                  classes={{
                    root: getRouteClassName(ROUTES.EMAIL_CAMPAIGN_REPORT),
                  }}
                >
                  <ListItemIcon className="listItemIcon">
                    <RpImageTextInline svgIcon="direct_mail_reports" />
                  </ListItemIcon>
                  <ListItemText primary="Email Campaign Reports" />
                </ListItemButton>
              ) : null}
            </>
          ) : null}
          {loggedInUserRole === USER_ROLE.BRAND_OWNER.NAME ||
          loggedInUserRole === USER_ROLE.BRAND_USER.NAME ||
          loggedInUserRole === USER_ROLE.PARENT_COMPANY.NAME ||
          loggedInUserRole === USER_ROLE.PARENT_COMPANY_USER.NAME ||
          loggedInUserRole === USER_ROLE.PARENT_COMPANY_SUB_USER.NAME ? (
            <>
              <ListItemButton
                component={NavLink}
                to={ROUTES.DIGITAL_CAMPAIGNS}
                classes={{
                  root: getRouteClassName(ROUTES.DIGITAL_CAMPAIGNS, [
                    ROUTES.DIGITAL_CAMPAIGNS,
                    ROUTES.DIGITAL_CAMPAIGN_REPORT,
                  ]),
                }}
              >
                <ListItemIcon className="listItemIcon">
                  <RpImageTextInline svgIcon="camp_reports" />
                </ListItemIcon>
                <ListItemText primary="Digital Campaigns" />
              </ListItemButton>

              <ListItemButton
                component={NavLink}
                to={ROUTES.DIRECT_MAIL}
                classes={{
                  root: getRouteClassName(ROUTES.DIRECT_MAIL, [
                    ROUTES.DIRECT_MAIL_REPORT,
                  ]),
                }}
              >
                <ListItemIcon className="listItemIcon">
                  <RpImageTextInline svgIcon="direct_mail_reports" />
                </ListItemIcon>
                <ListItemText primary="Direct Mail Report" />
              </ListItemButton>

              {(loggedInUserRole === USER_ROLE.BRAND_OWNER.NAME ||
                loggedInUserRole === USER_ROLE.BRAND_USER.NAME) &&
              EMAIL_CAMPAIGN_CONST.AVAILABLE_TO.includes(
                IS_PROD ? currentBrand?.id : "all"
              ) ? (
                <ListItemButton
                  component={NavLink}
                  to={ROUTES.EMAIL_CAMPAIGN_REPORT}
                  classes={{
                    root: getRouteClassName(ROUTES.EMAIL_CAMPAIGN_REPORT),
                  }}
                >
                  <ListItemIcon className="listItemIcon">
                    <RpImageTextInline svgIcon="direct_mail_reports" />
                  </ListItemIcon>
                  <ListItemText primary="Email Campaign Reports" />
                </ListItemButton>
              ) : null}

              <ListItemButton
                component={NavLink}
                to={ROUTES.LOCATION_MANAGEMENT}
                classes={{
                  root: getRouteClassName(ROUTES.LOCATION_MANAGEMENT),
                }}
              >
                <ListItemIcon className="listItemIcon">
                  <RpImageTextInline svgIcon="loc_reports" />
                </ListItemIcon>
                <ListItemText primary="Location Management" />
              </ListItemButton>

              {currentBrand?.id !== BRAND_CONST.MS_BRAND_ID &&
                currentBrand?.id !== BRAND_CONST.KUMON_BRAND_ID && (
                  <ListItemButton
                    component={NavLink}
                    to={ROUTES.CAMPAIGN_OVERVIEW}
                    classes={{
                      root: getRouteClassName(ROUTES.CAMPAIGN_OVERVIEW, [
                        ROUTES.JOIN_CAMPAIGN,
                        ROUTES.JOIN_POSTCARD_CAMPAIGN,
                      ]),
                    }}
                  >
                    <ListItemIcon className="listItemIcon">
                      <RpImageTextInline svgIcon="camp_reports" />
                    </ListItemIcon>
                    <ListItemText primary="Campaign Library" />
                  </ListItemButton>
                )}
            </>
          ) : null}

          {/* </Collapse> */}
          {loggedInUserRole === USER_ROLE.BRAND_OWNER.NAME ||
          loggedInUserRole === USER_ROLE.BRAND_USER.NAME ||
          loggedInUserRole === USER_ROLE.PARENT_COMPANY.NAME ||
          loggedInUserRole === USER_ROLE.PARENT_COMPANY_USER.NAME ||
          loggedInUserRole === USER_ROLE.PARENT_COMPANY_SUB_USER.NAME ? (
            <ListItem>
              <ListItemText
                className="user-role nav-sections"
                primary="Subscription Management"
              />
            </ListItem>
          ) : null}
          {loggedInUserRole === USER_ROLE.BRAND_USER.NAME ||
          loggedInUserRole === USER_ROLE.BRAND_OWNER.NAME ||
          loggedInUserRole === USER_ROLE.PARENT_COMPANY.NAME ||
          loggedInUserRole === USER_ROLE.PARENT_COMPANY_USER.NAME ||
          loggedInUserRole === USER_ROLE.PARENT_COMPANY_SUB_USER.NAME ? (
            <ListItemButton
              component={NavLink}
              to={ROUTES.PROFILE}
              classes={{ root: getRouteClassName(ROUTES.PROFILE) }}
            >
              <ListItemIcon className="listItemIcon">
                <RpImageTextInline svgIcon="person" />
              </ListItemIcon>
              <ListItemText primary="My Profile" />
            </ListItemButton>
          ) : null}

          {(loggedInUserRole === USER_ROLE.BRAND_USER.NAME &&
            currentBrand?.id !== 26) ||
          loggedInUserRole === USER_ROLE.BRAND_OWNER.NAME ||
          loggedInUserRole === USER_ROLE.PARENT_COMPANY.NAME ||
          loggedInUserRole === USER_ROLE.PARENT_COMPANY_USER.NAME ||
          loggedInUserRole === USER_ROLE.PARENT_COMPANY_SUB_USER.NAME ? (
            <>
              <ListItemButton
                component={NavLink}
                to={ROUTES.SUBSCRIPTIONS}
                classes={{
                  root: getRouteClassName(ROUTES.SUBSCRIPTIONS),
                }}
              >
                <ListItemIcon className="listItemIcon">
                  <RpImageTextInline svgIcon="subs_overview" />
                </ListItemIcon>
                <ListItemText primary="Media Plan Overview" />
              </ListItemButton>
            </>
          ) : null}
          {(loggedInUserRole === USER_ROLE.BRAND_USER.NAME ||
            loggedInUserRole === USER_ROLE.BRAND_OWNER.NAME ||
            loggedInUserRole === USER_ROLE.PARENT_COMPANY.NAME ||
            loggedInUserRole === USER_ROLE.PARENT_COMPANY_USER.NAME ||
            loggedInUserRole === USER_ROLE.PARENT_COMPANY_SUB_USER.NAME) &&
          currentBrand?.has_active_subscription_page ? (
            <>
              <ListItemButton
                component={NavLink}
                to={ROUTES.SUBSCRIPTION_PACKAGES}
                classes={{
                  root: getRouteClassName(ROUTES.SUBSCRIPTION_PACKAGES),
                }}
              >
                <ListItemIcon className="listItemIcon">
                  <RpImageTextInline svgIcon="add_subscription" />
                </ListItemIcon>
                <ListItemText primary="Add Media Plan" />
              </ListItemButton>
              <ListItemButton
                component={NavLink}
                to={ROUTES.MANAGE_SUBSCRIPTION}
                classes={{
                  root: getRouteClassName(ROUTES.MANAGE_SUBSCRIPTION),
                }}
              >
                <ListItemIcon className="listItemIcon">
                  <RpImageTextInline svgIcon="current_subscription" />
                </ListItemIcon>
                <ListItemText primary="Current Media Plan" />
              </ListItemButton>
            </>
          ) : null}
          {isParticipantUser && (
            <>
              <ListItem>
                <ListItemText
                  className="user-role nav-sections"
                  primary="Account Management"
                />
              </ListItem>
            </>
          )}
          {isParticipantUser ? (
            <ListItemButton
              component={NavLink}
              to={ROUTES.PROFILE}
              classes={{ root: getRouteClassName(ROUTES.PROFILE) }}
            >
              <ListItemIcon className="listItemIcon">
                <RpImageTextInline svgIcon="person" />
              </ListItemIcon>
              <ListItemText primary="My Profile" />
            </ListItemButton>
          ) : null}
          {canShowParticipantUserManagement ? (
            <ListItemButton
              component={NavLink}
              to={ROUTES.USER_MANAGEMENT}
              classes={{ root: getRouteClassName(ROUTES.USER_MANAGEMENT) }}
            >
              <ListItemIcon className="listItemIcon">
                <RpImageTextInline svgIcon="user_management_nav" />
              </ListItemIcon>
              <ListItemText primary="User Management" />
            </ListItemButton>
          ) : null}

          {isParticipantUser && currentBrand?.has_active_subscription_page ? (
            <>
              <ListItemButton
                component={NavLink}
                to={ROUTES.SUBSCRIPTION_PACKAGES}
                classes={{
                  root: getRouteClassName(ROUTES.SUBSCRIPTION_PACKAGES),
                }}
              >
                <ListItemIcon className="listItemIcon">
                  <RpImageTextInline svgIcon="add_subscription" />
                </ListItemIcon>
                <ListItemText primary="Add Media Plan" />
              </ListItemButton>
              <ListItemButton
                component={NavLink}
                to={ROUTES.MANAGE_SUBSCRIPTION}
                classes={{
                  root: getRouteClassName(ROUTES.MANAGE_SUBSCRIPTION),
                }}
              >
                <ListItemIcon className="listItemIcon">
                  <RpImageTextInline svgIcon="current_subscription" />
                </ListItemIcon>
                <ListItemText primary="Current Media Plan" />
              </ListItemButton>
            </>
          ) : null}

          {isParticipantUser && (
            <>
              {currentBrand?.id !== 1 ? (
                <ListItemButton
                  component={NavLink}
                  to={ROUTES.SUBSCRIPTION_INVOICE}
                  classes={{
                    root: getRouteClassName(ROUTES.SUBSCRIPTION_INVOICE),
                  }}
                >
                  <ListItemIcon className="listItemIcon">
                    <RpImageTextInline svgIcon="current_subscription" />
                  </ListItemIcon>
                  <ListItemText primary="Media Plan History" />
                </ListItemButton>
              ) : null}
              {currentBrand?.id !== 1 && currentBrand?.id !== 2 ? (
                <ListItemButton
                  component={NavLink}
                  to={ROUTES.PAYMENTS}
                  classes={{
                    root: getRouteClassName(ROUTES.PAYMENTS),
                  }}
                >
                  <ListItemIcon className="listItemIcon">
                    <RpImageTextInline svgIcon="manage_payment" />
                  </ListItemIcon>
                  <ListItemText primary="Payments" />
                </ListItemButton>
              ) : null}
            </>
          )}
          {settings?.brandSettings?.has_brand_resource ? (
            <>
              <ListItem>
                <ListItemText
                  className="user-role nav-sections"
                  primary="Resources"
                />
              </ListItem>
              <ListItemButton
                component={NavLink}
                to={`/education-center/${currentBrand?.id}`}
                classes={{
                  root: getRouteClassName(
                    `/education-center/${currentBrand?.id}`
                  ),
                }}
              >
                <ListItemIcon className="listItemIcon">
                  <RpImageTextInline svgIcon="resource_center" />
                </ListItemIcon>
                <ListItemText primary="Education Center" />
              </ListItemButton>
            </>
          ) : null}

          {isParticipantUser && (
            <>
              <ListItemButton className="listItem" onClick={handleOpenDialog}>
                <ListItemIcon className="listItemIcon">
                  <RpImageTextInline svgIcon="feedback" />
                </ListItemIcon>
                <ListItemText primary="Feedback" />
              </ListItemButton>
            </>
          )}

          {(loggedInUserRole === USER_ROLE.BRAND_OWNER.NAME ||
            loggedInUserRole === USER_ROLE.BRAND_USER.NAME ||
            loggedInUserRole === USER_ROLE.PARENT_COMPANY.NAME ||
            loggedInUserRole === USER_ROLE.PARENT_COMPANY_USER.NAME ||
            loggedInUserRole === USER_ROLE.PARENT_COMPANY_SUB_USER.NAME) &&
          currentBrand?.is_brand_request ? (
            <>
              <ListItemButton
                component={NavLink}
                to={`/requests`}
                classes={{ root: getRouteClassName("/requests") }}
              >
                <ListItemIcon className="listItemIcon">
                  <RpImageTextInline svgIcon="rocket" />
                </ListItemIcon>
                <ListItemText primary="Request" />
              </ListItemButton>
            </>
          ) : null}

          {CREATE_CAMPAIGN_ALLOWED_BRAND.includes(
            currentBrand?.system_generated_brand_id
          ) && // displayed only for Bojangles brand
            (loggedInUserRole === USER_ROLE.BRAND_OWNER.NAME ||
              loggedInUserRole === USER_ROLE.BRAND_USER.NAME) && (
              <ListItemButton
                component={NavLink}
                to={ROUTES.CREATE_CAMPAIGN}
                classes={{ root: getRouteClassName(ROUTES.CREATE_CAMPAIGN) }}
              >
                <ListItemIcon className="listItemIcon">
                  <RpImageTextInline svgIcon="file_nav" />
                </ListItemIcon>
                <ListItemText primary="Create Campaign" />
              </ListItemButton>
            )}
        </List>
      </div>
    </LayoutWrapper>
  );

  const container =
    window !== undefined ? () => window.document.body : undefined;

  return (
    <>
      <nav className="drawer" aria-label="mailbox folders">
        {!!isDesktop && (
          <Drawer
            container={container}
            variant="temporary"
            anchor={theme.direction === "rtl" ? "right" : "left"}
            open={mobileOpen}
            onClose={handleDrawerToggle}
            slotProps={{
              paper: {
                sx: {
                  width: drawerWidth,
                  boxSizing: "border-box",
                  border: "none",
                  boxShadow: "var(--shadow-overlay)",
                  backgroundColor: "var(--palette-navigation-surface)",
                  color: "var(--palette-navigation-text)",
                  padding: "0px 9px !important",
                  overflowX: "hidden",
                },
              },
            }}
            ModalProps={{
              keepMounted: true,
            }}
          >
            {drawer}
          </Drawer>
        )}
        {!isDesktop && (
          <Drawer
            slotProps={{
              paper: {
                sx: {
                  width: drawerWidth,
                  boxSizing: "border-box",
                  border: "none",
                  boxShadow: "none",
                  borderRight: "1px solid var(--neutral-200)",
                  backgroundColor: "var(--palette-navigation-surface)",
                  color: "var(--palette-navigation-text)",
                  padding: "0px 9px !important",
                  overflowX: "hidden",
                },
              },
            }}
            variant="permanent"
            open
          >
            {drawer}
          </Drawer>
        )}
      </nav>
      <Feedback open={FeedbackFormOpen} handleClose={handleCloseDialog} />
    </>
  );
}

export default ResponsiveDrawer;
